import React, { FunctionComponent } from 'react';
import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';

import {
  hasCanUsePlatesPermission,
  hasHangerUpgradeAndAnalyzeFeatureFlag,
  hasTrussConversionPermission,
  isLoggedInUserEngineer,
} from '@paragon/login-tools';

import { LockOptions, setLockOptionForComponentDesign } from '../../store/actions/lockOptionsActions';
import { useAppDispatch, useAppSelector } from '../hooks';

type OwnProps = {
  componentDesignGuid: string;
  lockOptions: LockOptions;
  lockedHangerNames: string[];
  lockedMemberNames: string[];
  lockedPlatePairNames: string[];
};

export const LockOptionsCheckboxes: FunctionComponent<OwnProps> = ({
  componentDesignGuid,
  lockOptions,
  lockedHangerNames,
  lockedMemberNames,
  lockedPlatePairNames,
}) => {
  const dispatch = useAppDispatch();
  const { analogLocked, hangersLocked, platingLocked, loadingLocked, membersLocked } = lockOptions;
  const isEngineer = useAppSelector(isLoggedInUserEngineer);
  const hasPermissionToConvertTrusses = useAppSelector(hasTrussConversionPermission);
  const allowHangerLocking = useAppSelector(hasHangerUpgradeAndAnalyzeFeatureFlag);
  const canUsePlates = useAppSelector(hasCanUsePlatesPermission);

  const lockHangersControl = (
    <FormControlLabel
      label="Lock Hangers"
      control={
        <Checkbox
          checked={hangersLocked}
          indeterminate={!hangersLocked && lockedHangerNames.length > 0}
          onChange={(event) =>
            dispatch(setLockOptionForComponentDesign(componentDesignGuid, 'hangersLocked', event.target.checked))
          }
        />
      }
    />
  );

  const lockLumberControl = (
    <FormControlLabel
      label="Lock Lumber"
      control={
        <Checkbox
          checked={membersLocked}
          indeterminate={!membersLocked && lockedMemberNames.length > 0}
          onChange={(event) =>
            dispatch(setLockOptionForComponentDesign(componentDesignGuid, 'membersLocked', event.target.checked))
          }
        />
      }
    />
  );

  const lockPlatingControl = (
    <FormControlLabel
      label="Lock Plating"
      control={
        <Checkbox
          checked={platingLocked}
          indeterminate={!platingLocked && lockedPlatePairNames.length > 0}
          onChange={(event) =>
            dispatch(setLockOptionForComponentDesign(componentDesignGuid, 'platingLocked', event.target.checked))
          }
        />
      }
    />
  );

  return (
    <>
      {isEngineer && (
        <FormControlLabel
          label="Lock Analog"
          control={
            <Checkbox
              // A special case: the analog can't be unlocked if loading is locked (https://trello.com/c/8aJGloOU)
              disabled={!isEngineer || loadingLocked}
              checked={analogLocked}
              onChange={(event) =>
                dispatch(setLockOptionForComponentDesign(componentDesignGuid, 'analogLocked', event.target.checked))
              }
            />
          }
        />
      )}
      <FormControlLabel
        label="Lock Loading"
        control={
          <Checkbox
            disabled={!isEngineer && !hasPermissionToConvertTrusses}
            checked={loadingLocked}
            onChange={(event) =>
              dispatch(setLockOptionForComponentDesign(componentDesignGuid, 'loadingLocked', event.target.checked))
            }
          />
        }
      />
      {canUsePlates ? (
        lockedPlatePairNames.length > 0 ? (
          <Tooltip disableInteractive title={lockedPlatePairNames.join(', ')}>
            {lockPlatingControl}
          </Tooltip>
        ) : (
          lockPlatingControl
        )
      ) : null}
      {lockedMemberNames.length > 0 ? (
        <Tooltip disableInteractive title={lockedMemberNames.join(', ')}>
          {lockLumberControl}
        </Tooltip>
      ) : (
        lockLumberControl
      )}
      {allowHangerLocking &&
        (lockedHangerNames.length > 0 ? (
          <Tooltip disableInteractive title={lockedHangerNames.join(', ')}>
            {lockHangersControl}
          </Tooltip>
        ) : (
          lockHangersControl
        ))}
    </>
  );
};
